/**
 * Capability matrix — fine-grained gates the UI uses to decide
 * whether to render a button / open a screen / submit a form.
 *
 * Each capability is a stable string like `'transaction.write'`
 * or `'project.delete'`. A role gets a set of capabilities;
 * `can(role, cap)` returns true/false.
 *
 * Why capabilities exist (vs raw role checks): UI code can ask
 * "can this user post a transaction?" without caring whether the
 * answer involves Admin, Manager, Accountant, or some new role
 * added next year. Add the role to the relevant capability set
 * here, and every gate updates.
 *
 * Server-side rules (`firestore.rules`) and Cloud Functions
 * (`setMemberRole`, `invites`, etc.) enforce the SAME buckets via
 * the helpers in `./helpers.ts`. The three layers stay aligned
 * because they all read from this file.
 */
import type { RoleKey } from './roles';

/** Capability strings used everywhere we gate a UI action. */
export type Capability =
  | 'studio.edit'
  | 'team.manage'
  /**
   * Manage the studio's plan & subscription. STUDIO OWNER
   * (Super Admin) ONLY — even Admins can't subscribe.
   *
   * The purchase is tied to whatever payment method is signed in
   * on the device performing the tap. If two admins could both
   * upgrade, you'd get duplicate charges or a confusing
   * entitlement-ownership state on the org.
   */
  | 'billing.manage'
  | 'project.create'
  | 'project.edit'
  | 'project.delete'
  | 'task.write'
  | 'task.update.own'
  | 'transaction.write'
  | 'transaction.read'
  /** Submit payment rows pending approval (site roles). */
  | 'transaction.submit'
  /** Approve/reject pending transactions. */
  | 'transaction.approve'
  /** Narrow client-only read: payment_in transactions on
   *  projects the caller is in `clientUids` of. */
  | 'transaction.read.payment_in.own_project'
  | 'dpr.write'
  | 'attendance.write'
  | 'material.request.write'
  | 'material.request.approve'
  /** Org-wide material catalogue write. SA/Admin only — the
   *  Firestore rule + the UI gate both use `canManageProjects`,
   *  which excludes Manager and Site Engineer. */
  | 'materialLibrary.write'
  /** Org-wide task catalogue write. SA/Admin only — same
   *  rationale as `materialLibrary.write`. */
  | 'taskLibrary.write'
  | 'design.write'
  | 'laminate.write'
  | 'whiteboard.write'
  | 'party.write'
  | 'crm.write'
  | 'report.read'
  | 'finance.read'
  | 'finance.write';

/** Operational capabilities granted to ALL admin-tier roles
 *  (Super Admin AND Admin). Manager + below get explicit
 *  subsets below. */
const ALL_CAPS: Capability[] = [
  'studio.edit',
  'team.manage',
  'project.create',
  'project.edit',
  'project.delete',
  'task.write',
  'task.update.own',
  'transaction.write',
  'transaction.read',
  'transaction.submit',
  'transaction.approve',
  'transaction.read.payment_in.own_project',
  'dpr.write',
  'attendance.write',
  'material.request.write',
  'material.request.approve',
  'materialLibrary.write',
  'taskLibrary.write',
  'design.write',
  'laminate.write',
  'whiteboard.write',
  'party.write',
  'crm.write',
  'report.read',
  'finance.read',
  'finance.write',
];

/** Capabilities granted ONLY to the Studio Owner (Super Admin),
 *  even when other privileged roles like Admin get everything
 *  else. Today this is just billing. */
const OWNER_ONLY_CAPS: Capability[] = ['billing.manage'];

function capsFor(role: RoleKey): Set<Capability> {
  switch (role) {
    case 'superAdmin':
      return new Set<Capability>([...ALL_CAPS, ...OWNER_ONLY_CAPS]);

    case 'admin':
      // Admin gets every operational capability EXCEPT the
      // owner-only ones (billing).
      return new Set(ALL_CAPS);

    case 'manager':
      // Manager = the studio's operating lieutenant. Has every
      // operational capability + writes + approves transactions +
      // manages staff in roles below them. Excluded from billing
      // (owner-only), studio profile edit (SA/Admin only), project
      // delete (SA/Admin only), and granting peer-or-higher roles
      // (peer protection enforced server-side by setMemberRole).
      return new Set<Capability>([
        'project.create',
        'project.edit',
        // Note: `project.delete` is deliberately NOT here —
        // destructive action stays with SA/Admin.
        'task.write',
        'task.update.own',
        // Manager has FULL transaction powers per the expanded
        // spec — can write payments AND approve pending
        // submissions from Site Engineer / Supervisor.
        'transaction.write',
        'transaction.read',
        'transaction.approve',
        'dpr.write',
        'attendance.write',
        'material.request.write',
        'material.request.approve',
        // Note: `materialLibrary.write` + `taskLibrary.write`
        // are NOT granted to Manager — both Firestore rule +
        // existing web gates use `canManageProjects`-equivalent
        // check that excludes them. SA/Admin only.
        'design.write',
        'laminate.write',
        'whiteboard.write',
        'party.write',
        'crm.write',
        'report.read',
        // Lets the +Add Member button show; server callable
        // enforces which roles Manager may grant + which
        // members they may edit (sub-roles only, per
        // ASSIGNABLE_ROLES_BY_MANAGER).
        'team.manage',
        // Finance hub access — unlocks the Finance bottom tab
        // and its Expenses / Staff / Payroll / Attendance
        // sub-tabs. Dashboard sub-tab is hidden for Manager via
        // a separate role-based segment filter.
        'finance.read',
        'finance.write',
      ]);

    case 'accountant':
      return new Set<Capability>([
        'transaction.write',
        'transaction.read',
        'party.write',
        'report.read',
        'finance.read',
        'finance.write',
      ]);

    case 'siteEngineer':
      return new Set<Capability>([
        'task.write',
        'task.update.own',
        'dpr.write',
        'attendance.write',
        'material.request.write',
        // Note: `materialLibrary.write` + `taskLibrary.write`
        // are NOT granted — Firestore rule + UI gates restrict
        // both to SA/Admin (`canManageProjects` check).
        'design.write',
        'laminate.write',
        'whiteboard.write',
        'transaction.submit',
        'transaction.read',
        // Submit-only roles need to be able to add a party
        // inline when raising an expense.
        'party.write',
      ]);

    case 'supervisor':
      return new Set<Capability>([
        'task.update.own',
        'dpr.write',
        'attendance.write',
        'material.request.write',
        'transaction.submit',
        'transaction.read',
        'party.write',
      ]);

    case 'designer':
      // Pure design-stack: timeline (task list), whiteboard,
      // laminates, designs/files. No finance / no CRM / no
      // attendance / no DPR / no materials / no party writes.
      return new Set<Capability>([
        'task.write',
        'task.update.own',
        'design.write',
        'laminate.write',
        'whiteboard.write',
      ]);

    case 'viewer':
      // Viewer can submit DPRs (project-scoped daily reports) —
      // only client and accountant are excluded from that.
      return new Set<Capability>([
        'transaction.read',
        'report.read',
        'dpr.write',
      ]);

    case 'client':
      // Clients are read-only stakeholders on a single project.
      // The ONLY finance data they get is incoming payments on
      // their project.
      return new Set<Capability>([
        'transaction.read.payment_in.own_project',
      ]);

    default: {
      const _exhaustive: never = role;
      return _exhaustive;
    }
  }
}

export const PERMISSIONS: Record<RoleKey, Set<Capability>> = {
  superAdmin: capsFor('superAdmin'),
  admin: capsFor('admin'),
  manager: capsFor('manager'),
  accountant: capsFor('accountant'),
  siteEngineer: capsFor('siteEngineer'),
  supervisor: capsFor('supervisor'),
  designer: capsFor('designer'),
  viewer: capsFor('viewer'),
  client: capsFor('client'),
};

/** True when the role has the capability. Returns false when
 *  role is null. */
export function can(
  role: RoleKey | null | undefined,
  cap: Capability,
): boolean {
  if (!role) return false;
  return PERMISSIONS[role].has(cap);
}
